import { motion } from "framer-motion";
import { springs } from "@/components/ui";
import type { UIAudit } from "@/packages/types";
import { AuditCard } from "./AuditCard";

// ─── Props ───────────────────────────────────────────────────────────────────

interface AuditGridViewProps {
  audits: UIAudit[];
  onOpen: (id: string) => void;
}

// ─── Stagger constants ───────────────────────────────────────────────────────

/** Max cards that receive incremental delay (avoids long waits on big lists) */
const MAX_STAGGER_INDEX = 12;
/** Seconds between each card entrance */
const STAGGER_STEP = 0.04;

// ─── Component ───────────────────────────────────────────────────────────────

export function AuditGridView({ audits, onOpen }: AuditGridViewProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={springs.snappy}
      className="h-full overflow-auto"
    >
      {/* Responsive grid: 1 col → 2 cols @640px → 3 cols @1024px */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-1">
        {audits.map((audit, index) => (
          <motion.div
            key={audit.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              ...springs.smooth,
              delay: Math.min(index, MAX_STAGGER_INDEX) * STAGGER_STEP,
            }}
          >
            <AuditCard audit={audit} onClick={onOpen} />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}
